"use server";

import { headers } from "next/headers";
import bcrypt from "bcryptjs";
import { z } from "zod";
import { prisma } from "@/lib/prisma";
import { sendMail } from "@/lib/mail";
import { appUrl } from "@/lib/app-url";
import {
  allowPasswordResetRequest,
  consumePasswordResetTokens,
  createPasswordResetToken,
  getValidPasswordReset,
} from "@/lib/password-reset";
import { getLocale } from "next-intl/server";
import { redirect } from "@/i18n/navigation";
import type { AppLocale } from "@/i18n/routing";

export type PasswordResetState = {
  error?: string;
  success?: boolean;
};

const emailSchema = z.string().trim().email();
const newPasswordSchema = z.object({
  token: z.string().min(16).max(128),
  newPassword: z.string().min(8).max(200),
  confirmPassword: z.string().min(1),
});

async function requestMeta() {
  const h = await headers();
  return (
    h.get("x-forwarded-for")?.split(",")[0]?.trim() ||
    h.get("x-real-ip")?.trim() ||
    "unknown"
  );
}

export async function requestPasswordResetAction(
  _prev: PasswordResetState,
  formData: FormData,
): Promise<PasswordResetState> {
  const parsed = emailSchema.safeParse(formData.get("email"));
  if (!parsed.success) return { error: "emailInvalid" };

  const email = parsed.data.toLowerCase();
  const ip = await requestMeta();
  if (
    !allowPasswordResetRequest(`email:${email}`) ||
    !allowPasswordResetRequest(`ip:${ip}`)
  ) {
    return { success: true };
  }

  const user = await prisma.user.findUnique({
    where: { email },
    select: { id: true, email: true, locale: true, status: true, name: true },
  });
  if (user && user.status !== "DISABLED") {
    const token = await createPasswordResetToken(user.id);
    const locale = user.locale === "en" ? "en" : "nb";
    const resetUrl = await appUrl(`/${locale}/reset-password/${token}`);
    const subject =
      locale === "en"
        ? "Reset your Bjørkhaug Eiendom password"
        : "Tilbakestill passord – Bjørkhaug Eiendom";
    const text =
      locale === "en"
        ? [
            `Hi${user.name ? ` ${user.name}` : ""},`,
            "",
            "Use this link to choose a new password. It expires in two hours.",
            resetUrl,
            "",
            "If you did not ask for this, you can ignore the email.",
          ].join("\n")
        : [
            `Hei${user.name ? ` ${user.name}` : ""},`,
            "",
            "Bruk denne lenken for å velge et nytt passord. Den utløper om to timer.",
            resetUrl,
            "",
            "Hvis du ikke ba om dette, kan du se bort fra e-posten.",
          ].join("\n");
    await sendMail({ to: user.email, subject, text });
    await prisma.auditEvent.create({
      data: {
        actorId: user.id,
        action: "auth.password_reset.request",
        entityType: "User",
        entityId: user.id,
      },
    });
  }

  return { success: true };
}

export async function completePasswordResetAction(
  _prev: PasswordResetState,
  formData: FormData,
): Promise<PasswordResetState> {
  const parsed = newPasswordSchema.safeParse({
    token: formData.get("token") ?? "",
    newPassword: formData.get("newPassword") ?? "",
    confirmPassword: formData.get("confirmPassword") ?? "",
  });
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    if (issue?.path[0] === "newPassword") return { error: "passwordShort" };
    return { error: "invalid" };
  }
  if (parsed.data.newPassword !== parsed.data.confirmPassword) {
    return { error: "passwordMismatch" };
  }

  const access = await getValidPasswordReset(parsed.data.token);
  if (!access) return { error: "expired" };

  await prisma.$transaction([
    prisma.user.update({
      where: { id: access.userId },
      data: {
        passwordHash: await bcrypt.hash(parsed.data.newPassword, 12),
        status: access.user.status === "INVITED" ? "ACTIVE" : access.user.status,
      },
    }),
    prisma.passwordResetToken.update({
      where: { id: access.id },
      data: { usedAt: new Date() },
    }),
  ]);
  await consumePasswordResetTokens(access.userId);
  await prisma.auditEvent.create({
    data: {
      actorId: access.userId,
      action: "auth.password_reset.complete",
      entityType: "User",
      entityId: access.userId,
    },
  });

  const locale = ((await getLocale()) === "en" ? "en" : "nb") as AppLocale;
  redirect({ href: "/login?reset=1", locale });
  return { success: true };
}
